/**
 * `matchMedia` as a hook — Long-Day Review, Tier 1 (SHELL-14, MOB-4, MOB-14).
 *
 * The pinned-vs-drawer decision cannot be a pair of Tailwind variants: it needs
 * *both* a width and a height test (a phone in landscape is 844px wide and has
 * no business showing a pinned sidebar), and two arbitrary media variants on
 * the same property are decided by stylesheet order rather than by intent.
 * Deciding in JS also lets the shell render one nav instead of two, so the
 * closed drawer contributes no tab stops at all.
 */
import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (): void => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * Where the sidebar is pinned rather than a drawer.
 *
 * `lg` (1024) not `md`: at 768 — iPad portrait — a pinned 16rem sidebar left
 * 512px of content and 1.7 board columns (MOB-14). The height clause catches
 * landscape phones, which are wide enough to pass a width-only test and then
 * have 88px of vertical room for the content (MOB-4).
 */
export const PINNED_SIDEBAR_QUERY = '(min-width: 1024px) and (min-height: 501px)';

export function usePinnedSidebar(): boolean {
  return useMediaQuery(PINNED_SIDEBAR_QUERY);
}

/**
 * Where an *expanded* sidebar may push the content across, rather than float
 * over it — #51.
 *
 * Being pinned and being roomy are two different questions, and Slate used to
 * answer only the first. From 1024 up the sidebar was in the flow at whatever
 * width the reader last left it, so an expanded one at 1100px took 16rem of a
 * 1100px window and the card beside it went to 1.2 readable columns; the
 * measured cost is in CARD-18 and #51.
 *
 * 1220 is Brad's number and it is close to the arithmetic: 16rem of sidebar
 * plus the 1400px card page's own two tracks stop competing at about 1216.
 * Below it the rail stays 3.5rem in the flow and expanding floats — which is
 * the drawer's behaviour below 1024, minus losing the icons.
 */
export const ROOMY_SIDEBAR_QUERY = '(min-width: 1220px)';

export function useRoomySidebar(): boolean {
  return useMediaQuery(ROOMY_SIDEBAR_QUERY);
}
